import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from "@angular/router";
import { AdminService } from '../../admin.service';
import { NavigationService } from '../../../shared/services/navigation.service';
import { BreadcrumbService } from '../../../shared/breadcrumb/breadcrumb.service';
import { NotificationMessageService } from '../../../shared/notification-message/notification-message.service';

@Component({
	selector: 'app-site-edit-page',
	templateUrl: './site-edit-page.component.html',
	styleUrls: ['./site-edit-page.component.css']
})
export class SiteEditPageComponent implements OnInit, OnDestroy {

	site: any = {
		id: 0,
		siteid: 0,
		customerid: 0,
		isNew: false
	};

	map: any = {};

	ACTusers = <any>[];
	dealersInfo = <any>[];
	serviceDealersInfo = <any>[];
	truckDealersInfo = <any>[];
	OEMsInfo = <any>[];
	siteOwners = <any>[];

	// Fuzzy matching properties
	similarSites: any[] = [];
	selectedSiteMatch: any = null;
	showSiteMatchDropdown: boolean = false;
	siteNameSearchTimer: any;
	canCreateSite: boolean = true;

	constructor(
		private adminService: AdminService,
		private route: ActivatedRoute,
		private navigation: NavigationService,
		private breadcrumb: BreadcrumbService,
		private notificationMessageService: NotificationMessageService,
	) {
	}

	ngOnInit() {
		this.route.params.subscribe(
			params => {
				this.site.id = Number(params['siteId']);
				this.site.siteid = this.site.id;
				this.site.customerid = Number(params['customerId']);
				this.site.isNew = this.site.id == 0;
			}
		);
		if (this.route.snapshot.queryParams['source'] != "site-info") {
			this.breadcrumb.setBreadcrumb([
				{'label': 'Customers', 'url': '/customers'},
				{'label': 'Sites', 'url': '/sites/customerId/'+this.site.customerid},
				{'label': 'Edit', 'url': ''}
			]);
		}

		this.getSite();
	}

	getSite() {

		this.adminService.getSite(this.site).subscribe((data: any) => {
			let insDate = new Date(new Date().setHours(0, 0, 0, 0));

			if(!this.site.isNew) {

				this.site = data.list[0];
				this.map.latitude = this.site.latitude;
				this.map.longitude = this.site.longitude;
				insDate = new Date(new Date(this.site.installation_date).setHours(0, 0, 0, 0));
			}

			this.site.installation_date = insDate;

			if(data.ACTusers) {
				data.ACTusers.forEach((user) => {
					user.name = user.firstname + ' ' + user.lastname;
				});
				this.ACTusers = data.ACTusers;
			}

			if(data.OEMsInfo)
				this.OEMsInfo = data.OEMsInfo;

			if(data.dealersInfo) {
				this.dealersInfo = data.dealersInfo;
				this.serviceDealersInfo = [];
				this.serviceDealersInfo = this.dealersInfo.slice();
				this.truckDealersInfo = structuredClone(data.dealersInfo); // Seconday dealer same as dealers info
			}

			if(data.siteOwners)
				this.siteOwners = data.siteOwners;
		});
	}

	/**
	 * Handle site name changes and check for duplicates
	 */
	onSiteNameChange() {
		clearTimeout(this.siteNameSearchTimer);

		// Reset state
		this.similarSites = [];
		this.selectedSiteMatch = null;
		this.showSiteMatchDropdown = false;
		this.canCreateSite = true;

		// If site name is empty, allow creation
		if (!this.site.name || this.site.name.trim() === '') {
			return;
		}

		// Debounce the check - only check if site name has at least 2 characters
		if (this.site.name.trim().length >= 2) {
			this.siteNameSearchTimer = setTimeout(() => {
				this.checkForDuplicateSites();
			}, 800);
		}
	}

	/**
	 * Handle site address changes (when populated by Google Map)
	 * Triggers duplicate check when address is set
	 */
	onSiteAddressChange() {
		// Check for duplicates when address is populated
		this.checkForDuplicateSites();
	}

	/**
	 * Check for duplicate sites by address and name
	 * Called when site name or address changes
	 * Uses tiered approach: Backend validation → Client-side fallback → String matching
	 */
	checkForDuplicateSites() {
		// Only check if we have both name and address
		if (!this.site.name || !this.site.address || !this.site.customerid) {
			this.similarSites = [];
			this.showSiteMatchDropdown = false;
			this.canCreateSite = true;
			return;
		}

		// Attempt primary duplicate detection (full geographic validation)
		this.adminService.checkForDuplicateSites(
			this.site.name,
			this.site.address,
			this.site.customerid,
			this.site.id,
			this.site.city || '',
			this.site.state || '',
			this.site.zipcode || ''
		).subscribe((res: any) => {
			if (res.httpStatus === 'success' && res.duplicates && res.duplicates.length > 0) {
				this.similarSites = res.duplicates;
				this.showSiteMatchDropdown = true;
				this.canCreateSite = false;
			} else {
				this.similarSites = [];
				this.showSiteMatchDropdown = false;
				this.canCreateSite = true;
			}
		}, (error) => {
			// Primary method failed - attempt fallback duplicate detection
			console.warn('[DuplicateCheck] Primary method failed, attempting fallback:', error.message);
			this.checkForDuplicateSitesFallback();
		});
	}

	/**
	 * Fallback duplicate detection when backend service is unavailable
	 * Uses client-side string matching with normalization
	 * Cascades: Full match → Fuzzy match → Address match
	 */
	private checkForDuplicateSitesFallback() {
		if (!this.site.name || !this.site.customerid) {
			this.similarSites = [];
			this.showSiteMatchDropdown = false;
			this.canCreateSite = true;
			return;
		}

		// Fetch all sites for this customer
		this.adminService.getClientSites(this.site.customerid).subscribe(
			(response: any) => {
				const allSites = response || [];
				const duplicates = [];
				const normalizedInputName = this.normalizeSiteName(this.site.name);
				const normalizedInputAddress = (this.site.address || '').toLowerCase().trim();

				for (const site of allSites) {
					// Skip the site being edited
					if (this.site.id > 0 && site.id === this.site.id) {
						continue;
					}

					const normalizedSiteName = this.normalizeSiteName(site.name);
					const normalizedSiteAddress = (site.address || '').toLowerCase().trim();

					let matchType = '';
					let similarity = 0;

					// Check 1: Exact address match
					if (normalizedInputAddress && normalizedSiteAddress && normalizedInputAddress === normalizedSiteAddress) {
						matchType = 'address_exact';
						similarity = 100;
					}
					// Check 2: Exact name match (after normalization)
					else if (normalizedInputName && normalizedSiteName && normalizedInputName === normalizedSiteName) {
						matchType = 'name_exact';
						similarity = 100;
					}
					// Check 3: Substring match
					else if (normalizedInputName && normalizedSiteName &&
						(normalizedInputName.includes(normalizedSiteName) || normalizedSiteName.includes(normalizedInputName))) {
						const shorter = Math.min(normalizedInputName.length, normalizedSiteName.length);
						const longer = Math.max(normalizedInputName.length, normalizedSiteName.length);
						similarity = Math.round((shorter / longer) * 100);
						matchType = 'name_substring';
					}

					// Include matches with high confidence
					if ((matchType === 'address_exact') || (matchType === 'name_exact') ||
					    (matchType === 'name_substring' && similarity >= 80)) {
						duplicates.push({
							id: site.id,
							name: site.name,
							address: site.address,
							city: site.city || '',
							state: site.state || '',
							matchType: matchType,
							similarity: similarity,
							isCurrentSite: false,
							detectionMethod: 'fallback'
						});
					}
				}

				// Show results
				if (duplicates.length > 0) {
					this.similarSites = duplicates;
					this.showSiteMatchDropdown = true;
					this.canCreateSite = false;
					this.notificationMessageService.setMessage(
						'⚠️ Duplicate detection in limited mode. Similar sites found - please verify.'
					);
				} else {
					this.similarSites = [];
					this.showSiteMatchDropdown = false;
					this.canCreateSite = true;
					this.notificationMessageService.setMessage(
						'⚠️ Limited duplicate detection active (validation service unavailable)'
					);
				}
			},
			(fallbackError) => {
				// Even fallback failed - allow creation but warn user
				console.error('[DuplicateCheck] Fallback also failed:', fallbackError.message);
				this.similarSites = [];
				this.showSiteMatchDropdown = false;
				this.canCreateSite = true;
				this.notificationMessageService.setMessage(
					'⚠️ WARNING: Unable to check for duplicate sites. Please verify manually that this site does not already exist.'
				);
			}
		);
	}

	/**
	 * Normalize site name for comparison
	 * Converts: "Main & West St.", "MAIN & WEST ST", "main west st" → "main and west st"
	 */
	private normalizeSiteName(name: string): string {
		if (!name || typeof name !== 'string') return '';

		let normalized = name
			.toLowerCase()
			.trim()
			.replace(/\s+/g, ' '); // collapse multiple spaces

		// Convert & to "and"
		normalized = normalized.replace(/&/g, 'and');

		// Strip all punctuation and symbols (keep letters, digits, whitespace)
		normalized = normalized.replace(/[^a-z0-9\s]/g, '');

		// Collapse whitespace again after symbol removal
		normalized = normalized.replace(/\s+/g, ' ');

		// Remove leading articles
		normalized = normalized.replace(/^(the|a|an)\s+/i, '');

		// Remove trailing legal suffixes
		normalized = normalized.replace(/\s+(inc|llc|ltd|gmbh|ag|plc|ltda|pty\s*ltd|ibc)$/i, '');

		return normalized.trim();
	}

	/**
	 * Navigate back to Customers / Sites list
	 */
	returnToCustomersSites() {
		this.goBack();
	}

	/**
	 * Keep the original site name and proceed with creation
	 */
	keepOriginalSiteName() {
		this.selectedSiteMatch = null;
		this.showSiteMatchDropdown = false;
		this.canCreateSite = true;
	}

	/**
	 * Check if we're editing an existing site (not creating new)
	 */
	isEditingExistingSite(): boolean {
		return this.site.id > 0;
	}

	saveSite() {
		// Check if user needs to make a selection from the dropdown
		if (this.showSiteMatchDropdown && !this.selectedSiteMatch) {
			return; // Prevent save if matches exist but none selected
		}

		// Check if form is valid before proceeding
		if (!this.site.name || !this.site.country || !this.site.state || !this.site.address) {
			return; // Prevent save if required fields are missing
		}

		let tempSite: any = Object.assign({}, this.site);
		let insDate = new Date(tempSite.installation_date);
		let drPhoneNumberWhiteList = [];
		if (tempSite.dr_provider == "ENERNOC" && tempSite.dr_phones_whitelist) {
			for (let phone of tempSite.dr_phones_whitelist) {
				if (phone.value) {
					drPhoneNumberWhiteList.push(phone.value);
				}
			}
		}
		tempSite.dr_phones_whitelist = drPhoneNumberWhiteList;
		tempSite.installation_date = Math.floor((-insDate.getTimezoneOffset() * 60000 + insDate.getTime()) / 1000);

		// Map state → stateid and country → countryid BEFORE deleting them
		// Frontend receives: country and state (which are actually the numeric IDs from DB aliases)
		// Backend expects: countryid and stateid
		if (tempSite.state && !tempSite.stateid) {
			tempSite.stateid = tempSite.state;
		}
		if (tempSite.country && !tempSite.countryid) {
			tempSite.countryid = tempSite.country;
		}

		// Now safe to remove the alias fields
		delete tempSite.state;
		delete tempSite.country;

		// Clean CAP data: ensure cap_battery_ids is properly formatted
		// and cap_enabled is a boolean
		if (tempSite.hasOwnProperty('cap_enabled')) {
			tempSite.cap_enabled = !!tempSite.cap_enabled;
		}
		if (tempSite.hasOwnProperty('cap_battery_ids') && !Array.isArray(tempSite.cap_battery_ids)) {
			tempSite.cap_battery_ids = [];
		}

		// DEBUG: Log the site object being sent to backend
		console.log('[CAP_DEBUG] saveSite payload:', JSON.stringify(tempSite, null, 2));
		console.log('[CAP_DEBUG] CAP enabled:', tempSite.cap_enabled);
		console.log('[CAP_DEBUG] CAP battery IDs:', JSON.stringify(tempSite.cap_battery_ids));
		console.log('[CAP_DEBUG] Site name:', tempSite.name);
		console.log('[CAP_DEBUG] Country ID:', tempSite.countryid);
		console.log('[CAP_DEBUG] State ID:', tempSite.stateid);

		this.adminService.saveSite(tempSite).subscribe((res:any) => {
			console.log('[CAP_DEBUG] saveSite response success:', res);
			if (res.duplicateSite)
				return this.notificationMessageService.setMessage('translate|site.duplicate_site');

			this.goBack();
		}, (error:any) => {
			console.error('[CAP_DEBUG] saveSite error response:', error);
			console.error('[CAP_DEBUG] Error status:', error.status);
			console.error('[CAP_DEBUG] Error message:', error.message);
			if (error.error) {
				console.error('[CAP_DEBUG] Error body:', error.error);
			}
		});
	}

	goBack() {
		let defaultPage = ['/sites', 'customerId', this.site.customerid];
		if (this.route.snapshot.queryParams['source'] == "site-info") {
			defaultPage = ['/', this.site.customerid, this.site.siteid];
		}
		this.navigation.goBack(defaultPage);
	}

	ngOnDestroy() {
		clearTimeout(this.siteNameSearchTimer);
		this.breadcrumb.hide();
	}
}
